import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  Paper,
  Stack,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Cloud as CloudIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { getApi, postApi } from 'views/services/api';

const WhatsAppConfigurationWizard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 🏗️ TODOS LOS ESTADOS AL INICIO
  const [activeStep, setActiveStep] = useState(0);
  const [config, setConfig] = useState({
    // Básicos
    whatsappNumber: '',
    countryCode: '+58', // Default Venezuela, pero configurable
    hotelName: '',
    address: '',

    // Credenciales Meta Business
    accessToken: '',
    phoneNumberId: '',
    verifyToken: '',
    appId: '',
    appSecret: '',
    businessAccountId: '',

    // Personalización
    welcomeMessage: '',
    welcomePoints: 100,
    maxDailyMessages: 1000,
    enableBusinessHours: false,
    businessHours: {
      start: '09:00',
      end: '18:00',
      timezone: 'America/Caracas'
    },
    
    // IA
    useWhatsappAIAssistant: false,
    geminiApiKey: ''
  });

  // 🌍 Lista de países con códigos telefónicos
  const countries = [
    { code: '+1', name: 'Estados Unidos / Canadá', flag: '🇺🇸🇨🇦' },
    { code: '+52', name: 'México', flag: '🇲🇽' },
    { code: '+58', name: 'Venezuela', flag: '🇻🇪' },
    { code: '+57', name: 'Colombia', flag: '🇨🇴' },
    { code: '+51', name: 'Perú', flag: '🇵🇪' },
    { code: '+56', name: 'Chile', flag: '🇨🇱' },
    { code: '+54', name: 'Argentina', flag: '🇦🇷' },
    { code: '+55', name: 'Brasil', flag: '🇧🇷' },
    { code: '+593', name: 'Ecuador', flag: '🇪🇨' },
    { code: '+507', name: 'Panamá', flag: '🇵🇦' },
    { code: '+506', name: 'Costa Rica', flag: '🇨🇷' },
    { code: '+34', name: 'España', flag: '🇪🇸' },
    { code: '+33', name: 'Francia', flag: '🇫🇷' },
    { code: '+49', name: 'Alemania', flag: '🇩🇪' },
    { code: '+44', name: 'Reino Unido', flag: '🇬🇧' },
    { code: '+39', name: 'Italia', flag: '🇮🇹' },
    { code: '+81', name: 'Japón', flag: '🇯🇵' },
    { code: '+86', name: 'China', flag: '🇨🇳' },
    { code: '+91', name: 'India', flag: '🇮🇳' },
    { code: '+61', name: 'Australia', flag: '🇦🇺' }
  ];

  const [loading, setLoading] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTokens, setShowTokens] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // 🏨 DATOS DEL HOTEL
  const hotel = JSON.parse(localStorage.getItem('hotelData')) || {};

  // 🔧 Webhook URL dinámico mejorado
  const getWebhookUrl = () => {
    const currentDomain = window.location.origin;

    if (currentDomain.includes('localhost') || currentDomain.includes('127.0.0.1')) {
      return `${currentDomain}/api/whatsapp/webhook`;
    } else {
      return `https://app.segocloud.com/api/whatsapp/webhook`;
    }
  };

  const webhookUrl = getWebhookUrl();

  // 📂 PASOS DEL WIZARD
  const steps = [
    {
      label: 'Información Básica',
      icon: <BusinessIcon />,
      description: 'Configura los datos básicos de tu hotel',
      fields: ['whatsappNumber', 'hotelName', 'address']
    },
    {
      label: 'Credenciales Meta',
      icon: <SecurityIcon />,
      description: 'Conecta con Meta Business API',
      fields: ['accessToken', 'phoneNumberId', 'verifyToken', 'appId']
    },
    {
      label: 'Configuración Avanzada',
      icon: <SettingsIcon />,
      description: 'Personaliza la experiencia de tus clientes',
      fields: ['welcomeMessage', 'welcomePoints', 'businessHours']
    },
    {
      label: 'Configurar Webhook',
      icon: <CloudIcon />,
      description: 'Configura el webhook en Meta Business',
      fields: []
    },
    {
      label: 'Verificación',
      icon: <CheckCircleIcon />,
      description: 'Verifica que todo funcione correctamente',
      fields: []
    }
  ];

  // 🚀 USEEFFECT - SIEMPRE AL INICIO, SIN CONDICIONES
  useEffect(() => {
    const fetchConfig = async () => {
      // Validación interna, no externa
      if (!hotel?.hotelId) {
        console.log('ℹ️ No hay hotel ID disponible');
        return;
      }

      try {
        setLoading(true);
        console.log('📡 Cargando configuración para hotel:', hotel.hotelId);

        const response = await getApi(`api/whatsappConfig/viewallwhatsappconfig/${hotel.hotelId}`);

        if (response?.data?.whatsappConfigData && response.data.whatsappConfigData.length > 0) {
          const configData = response.data.whatsappConfigData[0];

          setConfig({
            whatsappNumber: configData.whatsappNumber || '',
            countryCode: configData.countryCode || '+58', // Default si no existe
            hotelName: configData.hotelName || hotel?.hotelName || hotel?.name || '',
            address: configData.address || '',
            accessToken: configData.accessToken || '',
            phoneNumberId: configData.phoneNumberId || '',
            verifyToken: configData.verifyToken || '',
            appId: configData.appId || '',
            appSecret: configData.appSecret || '',
            businessAccountId: configData.businessAccountId || '',
            welcomeMessage: configData.welcomeMessage || '',
            welcomePoints: configData.welcomePoints || 100,
            maxDailyMessages: configData.maxDailyMessages || 1000,
            enableBusinessHours: configData.enableBusinessHours || false,
            useWhatsappAIAssistant: configData.useWhatsappAIAssistant || false,
            geminiApiKey: configData.geminiApiKey || '',
            businessHours: configData.businessHours || {
              start: '09:00',
              end: '18:00',
              timezone: 'America/Caracas'
            }
          });
          setHasConfig(true);
          console.log('✅ Configuración cargada para hotel:', configData.hotelName);
        } else {
          setConfig((prev) => ({
            ...prev,
            hotelName: hotel?.hotelName || hotel?.name || '',
            welcomeMessage: `¡Hola! Bienvenido a ${hotel?.hotelName || hotel?.name || 'nuestro hotel'}`
          }));
          setHasConfig(false);
          console.log('ℹ️ No hay configuración previa para hotel:', hotel.hotelId);
        }
      } catch (error) {
        console.log('ℹ️ Error cargando configuración:', error);
        setConfig((prev) => ({
          ...prev,
          hotelName: hotel?.hotelName || hotel?.name || '',
          welcomeMessage: `¡Hola! Bienvenido a ${hotel?.hotelName || hotel?.name || 'nuestro hotel'}`
        }));
        setHasConfig(false);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [hotel?.hotelId, hotel?.hotelName, hotel?.name]);

  // 🔧 FUNCIONES DEL COMPONENTE
  const validateStep = (stepIndex) => {
    const stepErrors = {};
    const stepFields = steps[stepIndex].fields;

    stepFields.forEach((field) => {
      if (field === 'whatsappNumber') {
        const cleanNumber = config[field].replace(/\D/g, '');
        // Validación más flexible para números internacionales (7-15 dígitos)
        if (!cleanNumber || cleanNumber.length < 7 || cleanNumber.length > 15) {
          stepErrors[field] = 'Número de WhatsApp inválido (7-15 dígitos)';
        }
      } else if (field === 'accessToken') {
        if (!config[field] || config[field].length < 50) {
          stepErrors[field] = 'Access Token inválido';
        }
      } else if (field === 'phoneNumberId') {
        if (!config[field] || !/^\d+$/.test(config[field])) {
          stepErrors[field] = 'Phone Number ID debe ser numérico';
        }
      } else if (['hotelName', 'verifyToken', 'appId'].includes(field)) {
        if (!config[field] || config[field].trim().length < 3) {
          stepErrors[field] = `${field} es requerido`;
        }
      }
    });

    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  // En WhatsAppConfiguration.js

  // En WhatsAppConfiguration.js
  const verifyConfiguration = async () => {
    setLoading(true);
    showMessage('Verificando configuración...', 'info');

    try {
      // Primero verificar la configuración local
      const response = await postApi('api/whatsappConfig/test', {
        whatsappNumber: `${config.countryCode}${config.whatsappNumber}`
      });

      if (response.success) {
        // Si la verificación local es exitosa, configurar el webhook
        const webhookResponse = await postApi('api/whatsappConfig/webhook/configure', {
          hotelId: hotel.hotelId
        });

        if (webhookResponse.success) {
          showMessage('✅ Configuración y webhook verificados exitosamente', 'success');
          return true;
        } else {
          showMessage(`❌ Error configurando webhook: ${webhookResponse.message}`, 'error');
          return false;
        }
      } else {
        showMessage(`❌ Error: ${response.message}`, 'error');
        return false;
      }
    } catch (error) {
      showMessage(`❌ Error de verificación: ${error.response?.data?.error || error.message}`, 'error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    setLoading(true);

    try {
      const configData = {
        hotelId: hotel.hotelId,
        ...config,
        // Guardar número completo con código de país
        fullWhatsappNumber: `${config.countryCode}${config.whatsappNumber.replace(/\D/g, '')}`,
        whatsappNumber: config.whatsappNumber.replace(/\D/g, ''),
        countryCode: config.countryCode
      };

      const response = await postApi('api/whatsappConfig/add', configData);

      showMessage('🎉 ¡Configuración guardada exitosamente! Tu WhatsApp está listo.', 'success');
      setHasConfig(true);
    } catch (error) {
      showMessage(`❌ Error al guardar: ${error.response?.data?.error || error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep < 3 && validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    } else if (activeStep >= 3) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFieldChange = (field, value) => {
    if (field === 'businessHours') {
      setConfig((prev) => ({
        ...prev,
        businessHours: value
      }));
    } else {
      setConfig((prev) => ({ ...prev, [field]: value }));
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showMessage(`✅ ${label} copiado al portapapeles`, 'success');
  };

  const showMessage = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const toggleTokenVisibility = (field) => {
    setShowTokens((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // 🛡️ VALIDACIÓN DESPUÉS DE TODOS LOS HOOKS
  if (!hotel?.hotelId) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <WarningIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Error de Autenticación
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            No se encontraron datos del hotel. Por favor, inicie sesión nuevamente.
          </Typography>
          <Button variant="contained" color="error" onClick={() => (window.location.href = '/login')}>
            Ir a Login
          </Button>
        </Paper>
      </Container>
    );
  }

  if (loading && !hasConfig) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }} color="text.secondary">
            Cargando configuración...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header principal */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
          color: 'white',
          p: 4,
          mb: 4,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <WhatsAppIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h3" fontWeight="bold">
                {hasConfig ? 'Actualizar WhatsApp Business' : 'Configuración WhatsApp Business'}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {hasConfig ? `Modifica la configuración para ${hotel?.hotelName || hotel?.name}` : 'Conecta tu hotel con Meta Business API'}
              </Typography>
            </Box>
          </Stack>

          {hasConfig && (
            <Chip
              icon={<CheckCircleIcon />}
              label={`✅ WhatsApp activo • ${config.countryCode} ${config.whatsappNumber} • ${config.hotelName}`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Progreso del wizard */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={(activeStep / (steps.length - 1)) * 100} sx={{ height: 8, borderRadius: 4 }} />
        </Box>

        <Stepper activeStep={activeStep} orientation={isMobile ? 'vertical' : 'horizontal'} sx={{ mt: 2 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                icon={step.icon}
                sx={{
                  '& .MuiStepIcon-root': {
                    color: index <= activeStep ? 'primary.main' : 'grey.300',
                    fontSize: '1.5rem'
                  }
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {step.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Contenido principal */}
      <Card elevation={2} sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          {steps[activeStep].label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {steps[activeStep].description}
        </Typography>

        {/* Paso 0: Información Básica */}
        {activeStep === 0 && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                📱 Número de WhatsApp Business *
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>País</InputLabel>
                    <Select
                      value={config.countryCode}
                      label="País"
                      onChange={(e) => handleFieldChange('countryCode', e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <PublicIcon color="primary" />
                        </InputAdornment>
                      }
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography>{country.flag}</Typography>
                            <Typography>{country.code}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {country.name}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Número de WhatsApp"
                    value={config.whatsappNumber}
                    onChange={(e) => handleFieldChange('whatsappNumber', e.target.value)}
                    placeholder="4141234567"
                    error={!!errors.whatsappNumber}
                    helperText={errors.whatsappNumber || 'Ingresa el número sin código de país'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="primary" />
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            {config.countryCode}
                          </Typography>
                        </InputAdornment>
                      )
                    }}
                    required
                  />
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                📞 Número completo:{' '}
                <strong>
                  {config.countryCode} {config.whatsappNumber}
                </strong>
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Nombre del Hotel"
              value={config.hotelName}
              onChange={(e) => handleFieldChange('hotelName', e.target.value)}
              error={!!errors.hotelName}
              helperText={errors.hotelName || 'Este nombre aparecerá en los mensajes'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="primary" />
                  </InputAdornment>
                )
              }}
              required
            />

            <TextField
              fullWidth
              label="Dirección del Hotel"
              value={config.address}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              helperText="Dirección que aparecerá en las pre-reservaciones"
              multiline
              rows={2}
            />
          </Stack>
        )}

        {/* Paso 1: Credenciales Meta */}
        {activeStep === 1 && (
          <Stack spacing={3}>
            {/* Instrucciones */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <InfoIcon color="primary" />
                  <Typography variant="h6">📋 Instrucciones paso a paso</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      1. 🌐 Crear App en Meta Business:
                    </Typography>
                    <Stack component="ul" spacing={0.5} sx={{ pl: 2 }}>
                      <Typography component="li" variant="body2">
                        Ve a{' '}
                        <Button variant="text" href="https://developers.facebook.com" target="_blank" rel="noreferrer">
                          Meta for Developers
                        </Button>
                      </Typography>
                      <Typography component="li" variant="body2">
                        Crea nueva aplicación → Tipo Business
                      </Typography>
                      <Typography component="li" variant="body2">
                        Agrega producto WhatsApp Business API
                      </Typography>
                      <Typography component="li" variant="body2">
                        Configura tu número:{' '}
                        <strong>
                          {config.countryCode} {config.whatsappNumber}
                        </strong>
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      2. 🔑 Obtener credenciales:
                    </Typography>
                    <Stack component="ul" spacing={0.5} sx={{ pl: 2 }}>
                      <Typography component="li" variant="body2">
                        Copia el <strong>Access Token</strong> desde el dashboard
                      </Typography>
                      <Typography component="li" variant="body2">
                        Copia el <strong>Phone Number ID</strong>
                      </Typography>
                      <Typography component="li" variant="body2">
                        Copia el <strong>App ID</strong>
                      </Typography>
                      <Typography component="li" variant="body2">
                        Crea un <strong>Verify Token</strong> personalizado
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <TextField
              fullWidth
              label="Access Token"
              type={showTokens.accessToken ? 'text' : 'password'}
              value={config.accessToken}
              onChange={(e) => handleFieldChange('accessToken', e.target.value)}
              placeholder="EAAxxxxxxxxxxxxxxxxxxxxx"
              error={!!errors.accessToken}
              helperText={errors.accessToken || 'Token de acceso de tu aplicación'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => toggleTokenVisibility('accessToken')}>
                      {showTokens.accessToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              required
            />

            <TextField
              fullWidth
              label="Phone Number ID"
              value={config.phoneNumberId}
              onChange={(e) => handleFieldChange('phoneNumberId', e.target.value)}
              placeholder="123456789012345"
              error={!!errors.phoneNumberId}
              helperText={errors.phoneNumberId || 'ID numérico de tu WhatsApp en Meta Business'}
              required
            />

            <TextField
              fullWidth
              label="Verify Token"
              value={config.verifyToken}
              onChange={(e) => handleFieldChange('verifyToken', e.target.value)}
              placeholder="mi_token_secreto_123"
              error={!!errors.verifyToken}
              helperText={errors.verifyToken || 'Token personalizado para verificar webhook'}
              required
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="App ID"
                  value={config.appId}
                  onChange={(e) => handleFieldChange('appId', e.target.value)}
                  placeholder="1234567890123456"
                  error={!!errors.appId}
                  helperText={errors.appId || 'ID de tu aplicación'}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="App Secret (Opcional)"
                  type={showTokens.appSecret ? 'text' : 'password'}
                  value={config.appSecret}
                  onChange={(e) => handleFieldChange('appSecret', e.target.value)}
                  placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  helperText="Secreto de aplicación (recomendado)"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => toggleTokenVisibility('appSecret')}>
                          {showTokens.appSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        )}

        {/* Paso 2: Configuración Avanzada */}
        {activeStep === 2 && (
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Mensaje de Bienvenida"
              multiline
              rows={3}
              value={config.welcomeMessage}
              onChange={(e) => handleFieldChange('welcomeMessage', e.target.value)}
              placeholder={`¡Hola! Bienvenido a ${config.hotelName || 'nuestro hotel'}`}
              helperText="Mensaje que verán los clientes al escribir por primera vez"
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Puntos de Bienvenida"
                  type="number"
                  value={config.welcomePoints}
                  onChange={(e) => handleFieldChange('welcomePoints', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, step: 10 }}
                  helperText="Puntos que se otorgan a nuevos clientes"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Límite Diario de Mensajes"
                  type="number"
                  value={config.maxDailyMessages}
                  onChange={(e) => handleFieldChange('maxDailyMessages', parseInt(e.target.value) || 1000)}
                  inputProps={{ min: 100, step: 100 }}
                  helperText="Límite de mensajes por día"
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Checkbox
                  checked={config.enableBusinessHours}
                  onChange={(e) => handleFieldChange('enableBusinessHours', e.target.checked)}
                />
              }
              label="Habilitar Horario de Atención"
            />

            {config.enableBusinessHours && (
              <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Configuración de Horarios
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Hora de Inicio"
                      type="time"
                      value={config.businessHours.start}
                      onChange={(e) =>
                        handleFieldChange('businessHours', {
                          ...config.businessHours,
                          start: e.target.value
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Hora de Fin"
                      type="time"
                      value={config.businessHours.end}
                      onChange={(e) =>
                        handleFieldChange('businessHours', {
                          ...config.businessHours,
                          end: e.target.value
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* 🤖 Sego IA Config */}
            <Paper elevation={1} sx={{ p: 3, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Typography variant="h6" color="success.dark" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                🤖 Sego IA (Asistente Inteligente)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Reemplaza el bot tradicional por una Inteligencia Artificial que responde naturalmente y agenda reservaciones automáticamente usando tu propia llave de Gemini.
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.useWhatsappAIAssistant}
                    onChange={(e) => handleFieldChange('useWhatsappAIAssistant', e.target.checked)}
                    color="success"
                  />
                }
                label={<strong>Habilitar Asistente de IA (Requiere API Key de Gemini 2.5 Flash)</strong>}
                sx={{ mb: 2 }}
              />

              {config.useWhatsappAIAssistant && (
                <TextField
                  fullWidth
                  label="Gemini API Key"
                  type={showTokens.geminiApiKey ? 'text' : 'password'}
                  value={config.geminiApiKey}
                  onChange={(e) => handleFieldChange('geminiApiKey', e.target.value)}
                  placeholder="AIzaSyXXXXXXXXXXXXXXXX"
                  helperText="Se usarán los créditos de tu cuenta de Google Cloud para facturar."
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => toggleTokenVisibility('geminiApiKey')}>
                          {showTokens.geminiApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  required={config.useWhatsappAIAssistant}
                />
              )}
            </Paper>
          </Stack>
        )}

        {/* Paso 3: Configurar Webhook */}
        {activeStep === 3 && (
          <Stack spacing={3}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                🚨 PASO CRÍTICO: Configurar Webhook en Meta Business
              </Typography>
              <Typography variant="body2">
                Sin este paso, el WhatsApp NO funcionará. Meta debe poder enviar mensajes a nuestro servidor.
              </Typography>
            </Alert>

            <Paper elevation={3} sx={{ p: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
              <Typography variant="h6" gutterBottom>
                📍 En Meta Developer Console:
              </Typography>
              <Stack component="ol" spacing={1} sx={{ pl: 2 }}>
                <Typography component="li">Ve a tu App → WhatsApp → Configuration</Typography>
                <Typography component="li">En la sección Webhooks, haz clic en Edit</Typography>
                <Typography component="li">Ingresa estos datos exactos:</Typography>
              </Stack>
            </Paper>

            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                  Callback URL:
                </Typography>
                <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.100', wordBreak: 'break-all' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {webhookUrl}
                    </Typography>
                    <Tooltip title="Copiar URL">
                      <IconButton size="small" onClick={() => copyToClipboard(webhookUrl, 'URL del webhook')}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                  Verify Token:
                </Typography>
                <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.100' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {config.verifyToken || 'Primero completa el paso anterior'}
                    </Typography>
                    {config.verifyToken && (
                      <Tooltip title="Copiar Token">
                        <IconButton size="small" onClick={() => copyToClipboard(config.verifyToken, 'Verify Token')}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Paper>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                  Webhook Fields:
                </Typography>
                <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.100' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      messages
                    </Typography>
                    <Tooltip title="Copiar">
                      <IconButton size="small" onClick={() => copyToClipboard('messages', 'Campo de webhook')}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              </Box>
            </Stack>

            <Alert severity="info">
              <Typography variant="body2">
                💡 <strong>Tip:</strong> Después de configurar el webhook en Meta, continúa al siguiente paso para verificar que todo
                funciona.
              </Typography>
            </Alert>
          </Stack>
        )}

        {/* Paso 4: Verificación */}
        {activeStep === 4 && (
          <Stack spacing={3}>
            <Box textAlign="center">
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                🔍 Verificación Final
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Vamos a verificar que tu configuración funcione correctamente
              </Typography>
            </Box>

            <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom>
                📋 Resumen de Configuración:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Hotel:</strong> {config.hotelName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Número:</strong> {config.countryCode} {config.whatsappNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>País:</strong> {countries.find((c) => c.code === config.countryCode)?.name || 'País seleccionado'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>App ID:</strong> {config.appId}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Phone ID:</strong> {config.phoneNumberId}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Puntos:</strong> {config.welcomePoints}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Límite diario:</strong> {config.maxDailyMessages} mensajes
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={1} sx={{ p: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
              <Typography variant="h6" gutterBottom>
                ✅ URL del Webhook Configurada:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {webhookUrl}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                Esta URL debe estar configurada en Meta Business
              </Typography>
            </Paper>

            <Stack spacing={2}>
              <Button
                variant="outlined"
                size="large"
                onClick={verifyConfiguration}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SecurityIcon />}
                fullWidth
              >
                {loading ? 'Verificando...' : '🔍 Verificar Configuración'}
              </Button>

              <Button
                variant="contained"
                size="large"
                onClick={saveConfiguration}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                fullWidth
              >
                {loading ? 'Guardando...' : '💾 Guardar Configuración'}
              </Button>
            </Stack>
          </Stack>
        )}

        {/* Navegación */}
        <Divider sx={{ my: 4 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button onClick={handleBack} disabled={activeStep === 0} variant="outlined" sx={{ minWidth: 120 }}>
            ← Anterior
          </Button>

          <Chip label={`${activeStep + 1} de ${steps.length}`} color="primary" variant="outlined" />

          {activeStep < steps.length - 1 && (
            <Button onClick={handleNext} variant="contained" sx={{ minWidth: 120 }}>
              Siguiente →
            </Button>
          )}

          {activeStep === steps.length - 1 && (
            <Box sx={{ minWidth: 120 }} /> // Spacer
          )}
        </Stack>
      </Card>
    </Container>
  );
};

export default WhatsAppConfigurationWizard;
